import { t } from 'i18next';

import {
    normalizeCopilotChord,
    validateCopilotActions
} from 'app/modules/wallforming/components/copilot/utils/copilotCapabilities';
import {
    INPUT_BINDING_SLOTS,
    inputActions
} from 'app/modules/wallforming/components/input-binding/registry/inputActionsRegistry';
import {
    InputActionId,
    type InputActionId as InputActionIdType
} from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import { formatChordLabel } from 'app/modules/wallforming/components/input-binding/utils/inputChordUtils';
import { isFillerInstanceSelection } from 'app/modules/wallforming/components/scenes/objects/articles/utils/transformationUtils';
import { TransformationMode } from 'app/modules/wallforming/components/scenes/objects/helpers/types/transformationMode';
import { EPSILON } from 'shared/constants';

import type { OidcUserInfo } from '@axa-fr/oidc-client';
import type { ArticleGroupPlacement, PlacementSolution } from 'api/models';
import type {
    CopilotChatRequest,
    CopilotFloatingBounds,
    CopilotMessage,
    CopilotProposal,
    CopilotSummaryKey,
    CopilotWorkspaceAction
} from 'app/modules/wallforming/components/copilot/types/copilotTypes';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

export const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

const normalizeActionName = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

export const normalizeLatexDelimiters = (content: string): string =>
    content.replace(
        /(```[\s\S]*?```|~~~[\s\S]*?~~~|(`+)[\s\S]*?\2)|\\\[([\s\S]*?)\\\]|\\\(([\s\S]*?)\\\)/g,
        (
            match,
            codeBlockOrInlineCode: string | undefined,
            inlineCode: string | undefined,
            displayMath: string | undefined,
            inlineMath: string | undefined
        ) =>
            codeBlockOrInlineCode || inlineCode
                ? match
                : displayMath !== undefined
                  ? `$$\n${displayMath}\n$$`
                  : inlineMath !== undefined
                    ? `$${inlineMath}$`
                    : match
    );

export const resolveCopilotInputActionId = (value: string): InputActionIdType | null => {
    const normalizedValue = normalizeActionName(value);
    const matches = Object.entries(InputActionId).filter(([name, id]) => {
        const normalizedName = normalizeActionName(name);
        const normalizedId = normalizeActionName(id);
        return (
            normalizedName === normalizedValue ||
            normalizedId === normalizedValue ||
            normalizedName.endsWith(normalizedValue) ||
            normalizedId.endsWith(normalizedValue)
        );
    });
    const uniqueIds = [...new Set(matches.map(([, id]) => id))];
    return uniqueIds.length === 1 ? uniqueIds[0] : null;
};

export const getProposalContextSignature = (
    context: Pick<CopilotChatRequest['context'], 'sessionId' | 'systemId' | 'workspaceRevision'>
): string =>
    JSON.stringify({
        sessionId: context.sessionId,
        systemId: context.systemId,
        workspaceRevision: context.workspaceRevision
    });

export const getPlacements = (result: unknown): ArticleGroupPlacement[] | null => {
    if (!isRecord(result) || !Array.isArray(result.placements)) {
        return null;
    }

    if (
        !result.placements.every(
            (group) =>
                isRecord(group) &&
                typeof group.articleGuid === 'string' &&
                typeof group.articleNumber === 'string' &&
                typeof group.representationGuid === 'string' &&
                Array.isArray(group.instances)
        )
    ) {
        return null;
    }

    return result.placements as ArticleGroupPlacement[];
};

export const getCornerSolutions = (result: unknown): PlacementSolution[] | null => {
    if (!isRecord(result) || !Array.isArray(result.solutions)) {
        return null;
    }

    return result.solutions as PlacementSolution[];
};

export const getProposalSummary = (
    proposal: CopilotProposal
): { key: CopilotSummaryKey; values: Record<string, number> } | null => {
    switch (proposal.toolName) {
        case 'propose_workspace_actions':
        case 'propose_application_actions':
            return null;
        case 'solve_panel_placement':
        case 'solve_placement_workflow':
        case 'auto_form_building':
        case 'solve_connector_placement': {
            const placements = getPlacements(proposal.result);
            if (placements) {
                return {
                    key:
                        proposal.toolName === 'solve_connector_placement'
                            ? 'copilotConnectorPlacementSummary'
                            : 'copilotPanelSummary',
                    values: {
                        groups: placements.length,
                        instances: placements.reduce((total, group) => total + group.instances.length, 0)
                    }
                };
            }

            return proposal.toolName === 'solve_connector_placement'
                ? getConnectorProposalSummary(proposal.result)
                : null;
        }
        case 'solve_corner_placement': {
            const solutions = getCornerSolutions(proposal.result);
            return solutions ? { key: 'copilotCornerSummary', values: { solutions: solutions.length } } : null;
        }
        default:
            if (
                isRecord(proposal.result) &&
                Array.isArray(proposal.result.snapPoints) &&
                Array.isArray(proposal.result.snapRanges)
            ) {
                return {
                    key: 'copilotConnectorSummary',
                    values: {
                        snapPoints: proposal.result.snapPoints.length,
                        snapRanges: proposal.result.snapRanges.length
                    }
                };
            }
            return null;
    }
};

const getConnectorProposalSummary = (result: unknown) => {
    if (!isRecord(result) || !Array.isArray(result.snapPoints) || !Array.isArray(result.snapRanges)) {
        return null;
    }

    return {
        key: 'copilotConnectorSummary' as const,
        values: { snapPoints: result.snapPoints.length, snapRanges: result.snapRanges.length }
    };
};

export const getWorkspaceActionSummary = (proposal: CopilotProposal): string | null => {
    if (proposal.toolName === 'propose_application_actions' && isRecord(proposal.result)) {
        try {
            return validateCopilotActions(proposal.result.actions)
                .map((action) => `${action.type}: ${JSON.stringify(action.parameters)}`)
                .join(' · ');
        } catch {
            return 'Invalid Copilot action proposal';
        }
    }
    if (
        proposal.toolName !== 'propose_workspace_actions' ||
        !isRecord(proposal.result) ||
        !Array.isArray(proposal.result.actions)
    ) {
        return null;
    }

    const descriptions = proposal.result.actions
        .filter((action): action is Record<string, unknown> => isRecord(action) && typeof action.type === 'string')
        .map((action) => {
            switch (action.type) {
                case 'select_all':
                    return t('copilotActionSelectAll');
                case 'discard_selection':
                    return t('copilotActionClearSelection');
                case 'show_translation_tool':
                    return t('copilotActionShowTranslation');
                case 'show_rotation_tool':
                    return t('copilotActionShowRotation');
                case 'move_selection':
                    return t('copilotActionMoveSelection', {
                        axis: String(action.axis ?? '?').toUpperCase(),
                        meters: String(action.meters ?? '?')
                    });
                case 'scale_selection':
                    return t('copilotActionScaleSelection', {
                        axis: String(action.axis ?? '?').toUpperCase(),
                        factor: String(action.factor ?? '?')
                    });
                case 'set_input_binding': {
                    const chord = typeof action.chord === 'string' ? normalizeCopilotChord(action.chord) : null;
                    const label = chord ? formatChordLabel(chord) : String(action.chord ?? 'unbound');
                    return t('copilotActionSetInputBinding', {
                        actionId: String(action.actionId ?? 'input'),
                        slot: String(action.slot ?? '?'),
                        chord: label
                    });
                }
                default:
                    return null;
            }
        })
        .filter((description): description is string => description !== null);

    return descriptions.length > 3
        ? `${descriptions.slice(0, 3).join(' · ')} · +${descriptions.length - 3}`
        : descriptions.join(' · ');
};

export const getImageResourceBySize = (userInfo: OidcUserInfo, imageSize: 's' | 'md') =>
    userInfo[`picture_${imageSize}` as keyof OidcUserInfo]?.toString() ?? userInfo.picture;

export const buildConversationHistory = (
    messages: CopilotMessage[],
    maxMessages: number,
    maxCharacters: number,
    maxMessageCharacters: number
): Array<{ role: CopilotMessage['role']; content: string }> => {
    const history: Array<{ role: CopilotMessage['role']; content: string }> = [];
    let totalCharacters = 0;

    for (const message of messages.slice(-maxMessages).reverse()) {
        const receipts = message.proposals
            ?.filter((proposal) => proposal.applied || proposal.rejected)
            .map(
                (proposal) =>
                    `${proposal.toolName}: ${proposal.applied ? 'applied or opened for review' : 'rejected by user'}`
            );
        const content = receipts?.length
            ? `${message.content}\nWorkspace receipts: ${receipts.join('; ')}`
            : message.content;
        const remainingCharacters = maxCharacters - totalCharacters;
        if (remainingCharacters <= 0) {
            break;
        }

        const boundedContent = content.slice(-Math.min(maxMessageCharacters, remainingCharacters));
        if (boundedContent) {
            history.push({ role: message.role, content: boundedContent });
            totalCharacters += boundedContent.length;
        }
    }

    return history.reverse();
};

export const applyCopilotWorkspaceActions = (value: unknown, state: PlacementState): void => {
    if (state.isViewMode || !isRecord(value) || !Array.isArray(value.actions)) {
        throw new Error(t('copilotInvalidActionError'));
    }

    if (value.actions.length === 0 || value.actions.length > 8) {
        throw new Error(t('copilotInvalidActionError'));
    }

    const normalizedActions: CopilotWorkspaceAction[] = [];
    for (const candidate of value.actions) {
        const action = parseWorkspaceAction(candidate);
        if (!action) {
            throw new Error(t('copilotInvalidActionError'));
        }
        normalizedActions.push(action);
    }

    const hasTransformAction = normalizedActions.some(
        (action) => action.type === 'move_selection' || action.type === 'scale_selection'
    );
    if (hasTransformAction && (!state.arePivotControlsActive || !state.pivotControlsRef.current)) {
        throw new Error(t('copilotEnableTransformFirstError'));
    }

    if (
        normalizedActions.some((action) => action.type === 'scale_selection') &&
        !isFillerInstanceSelection(Object.keys(state.articleInstanceSelections), state)
    ) {
        throw new Error(t('copilotScaleFillerSelectionError'));
    }

    const firstMoveIndex = normalizedActions.findIndex((action) => action.type === 'move_selection');
    if (
        firstMoveIndex >= 0 &&
        Object.keys(state.articleInstanceSelections).length === 0 &&
        !normalizedActions.slice(0, firstMoveIndex).some((action) => action.type === 'select_all')
    ) {
        throw new Error(t('copilotNoSelectionToMoveError'));
    }

    for (const action of normalizedActions) {
        switch (action.type) {
            case 'select_all':
                state.selectAllArticleInstances();
                break;
            case 'discard_selection':
                state.discardArticleInstanceSelection(false);
                break;
            case 'show_translation_tool':
                state.setArePivotControlsActive(true);
                state.setArticleTransformationMode(TransformationMode.TRANSLATION);
                break;
            case 'show_rotation_tool':
                state.setArePivotControlsActive(true);
                state.setArticleTransformationMode(TransformationMode.ROTATION);
                break;
            case 'move_selection':
                state.applyTranslationValue(getAxisIndex(action.axis), action.meters);
                break;
            case 'scale_selection':
                state.applyScalingValue(getAxisIndex(action.axis), action.factor);
                break;
            case 'set_input_binding':
                state.assignInputBinding(action.actionId, action.slot, action.chord);
                break;
        }
    }
};

const parseWorkspaceAction = (value: unknown): CopilotWorkspaceAction | null => {
    if (!isRecord(value) || typeof value.type !== 'string') {
        return null;
    }

    switch (value.type) {
        case 'select_all':
        case 'discard_selection':
        case 'show_translation_tool':
        case 'show_rotation_tool':
            return { type: value.type };
        case 'move_selection':
            return isAxis(value.axis) &&
                typeof value.meters === 'number' &&
                Number.isFinite(value.meters) &&
                Math.abs(value.meters) <= 10
                ? { type: value.type, axis: value.axis, meters: value.meters }
                : null;
        case 'scale_selection':
            return isAxis(value.axis) &&
                typeof value.factor === 'number' &&
                Number.isFinite(value.factor) &&
                value.factor >= EPSILON
                ? { type: value.type, axis: value.axis, factor: value.factor }
                : null;
        case 'set_input_binding': {
            const actionId = typeof value.actionId === 'string' ? resolveCopilotInputActionId(value.actionId) : null;
            const slotIsValid =
                value.slot === undefined || (typeof value.slot === 'number' && Number.isInteger(value.slot));
            let chord: string | null;
            if (value.chord === null) {
                chord = null;
            } else if (typeof value.chord === 'string' && value.chord.length <= 64) {
                const normalizedChord = normalizeCopilotChord(value.chord);
                if (!normalizedChord) {
                    return null;
                }
                chord = normalizedChord;
            } else {
                return null;
            }
            if (!actionId || !slotIsValid) {
                return null;
            }

            const requestedSlot = typeof value.slot === 'number' ? value.slot : 0;
            const slot =
                requestedSlot >= 0 && requestedSlot < Math.min(INPUT_BINDING_SLOTS, inputActions[actionId].slots)
                    ? requestedSlot
                    : 0;
            return {
                type: value.type,
                actionId,
                slot,
                chord
            };
        }
        default:
            return null;
    }
};

const isAxis = (value: unknown): value is 'x' | 'y' | 'z' => value === 'x' || value === 'y' || value === 'z';

const getAxisIndex = (axis: 'x' | 'y' | 'z'): 0 | 1 | 2 => {
    switch (axis) {
        case 'x':
            return 0;
        case 'y':
            return 1;
        case 'z':
            return 2;
    }
};

export const getCopilotDockTargetBounds = () => {
    const target = document.querySelector<HTMLElement>('[data-copilot-dock-target]');
    if (!target) {
        return null;
    }

    const { left, top, width, height } = target.getBoundingClientRect();
    return { left, top, width, height };
};

export const overlapsCopilotDockTarget = (
    panel: Pick<CopilotFloatingBounds, 'x' | 'y' | 'width' | 'height'>,
    target: { left: number; top: number; width: number; height: number }
) => {
    const horizontalInset = Math.min(16, target.width * 0.4);
    const verticalInset = Math.min(16, target.height * 0.4);
    return (
        panel.x < target.left + target.width - horizontalInset &&
        panel.x + panel.width > target.left + horizontalInset &&
        panel.y < target.top + target.height - verticalInset &&
        panel.y + panel.height > target.top + verticalInset
    );
};
